import React, { useState, useEffect } from 'react';
import { Activity, Users, Clock, Shield, Globe, Zap, RefreshCw, AlertTriangle, CheckCircle, ExternalLink, Moon, Sun } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  Legend,
  CartesianGrid
} from 'recharts';

interface DemoStats {
  usage_tracking: {
    total_users: number;
    total_minutes_used: number;
    total_sessions_created: number;
    users_hit_limits: number;
    vpn_users: number;
    avg_minutes_per_user: number;
    avg_sessions_per_user: number;
  };
  sessions: {
    total_sessions: number;
    active_sessions: number;
    vpn_sessions: number;
    latest_session: string;
  };
  top_countries: Array<{
    country_code: string;
    session_count: number;
    minute_count: number;
  }>;
  conversion_metrics: {
    total_signups: number;
    conversion_rate: number;
    revenue_attributed: number;
  };
}

interface UserDetails {
  ip_hash: string;
  total_minutes: number;
  session_count: number;
  vpn_detected: boolean;
  fingerprint_hash: string;
  user_agent: string;
  country_code: string;
  first_session: string;
  last_session: string;
  limit_reached: boolean;
}

interface RecentActivity {
  session_id: string;
  ip_hash: string;
  host_name: string;
  guest_name: string;
  room_id: string;
  country_code: string;
  vpn_detected: boolean;
  user_agent: string;
  referrer?: string;
  created_at: string;
}

interface RequestOriginStats {
  referrers: Array<{
    referrer: string;
    count: number;
    percentage: number;
  }>;
  user_agents: Array<{
    user_agent: string;
    count: number;
    is_bot: boolean;
  }>;
  automation_tools: Array<{
    tool_name: string;
    count: number;
    last_seen: string;
  }>;
}

interface EndpointHealth {
  endpoint: string;
  status: 'healthy' | 'slow' | 'error';
  responseTime: number;
  lastChecked: string;
}

export const PublicDemoMonitor: React.FC = () => {
  const [stats, setStats] = useState<DemoStats | null>(null);
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [requestOrigins, setRequestOrigins] = useState<RequestOriginStats | null>(null);
  const [endpointHealth, setEndpointHealth] = useState<EndpointHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'activity' | 'origins'>('overview');
  const { theme, toggleTheme } = useTheme();

  const fetchStats = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/test/database-stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/test/users');
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/test/recent-activity');
      if (response.ok) {
        const data = await response.json();
        setRecentActivity(data.activities || []);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchRequestOrigins = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/test/request-origins');
      if (response.ok) {
        const data = await response.json();
        setRequestOrigins(data);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const testEndpoint = async (endpoint: string, method: string = 'GET', body?: any): Promise<EndpointHealth> => {
    const start = performance.now();
    try {
      const options: RequestInit = { method };
      if (body) {
        options.headers = { 'Content-Type': 'application/json' };
        options.body = JSON.stringify(body);
      }
      
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev${endpoint}`, options);
      const responseTime = performance.now() - start;
      
      let status: EndpointHealth['status'] = 'healthy';
      if (responseTime > 2000) status = 'slow';
      if (!response.ok) status = 'error';
      
      return {
        endpoint,
        status,
        responseTime: Math.round(responseTime),
        lastChecked: new Date().toISOString()
      };
    } catch (error) {
      return {
        endpoint,
        status: 'error',
        responseTime: performance.now() - start,
        lastChecked: new Date().toISOString()
      };
    }
  };

  const testAllEndpoints = async () => {
    const endpoints = [
      { path: '/public/demo/status/check', method: 'GET' },
      { path: '/public/demo/create-session', method: 'POST', body: { hostName: 'Monitor Test', guestName: 'Monitor Guest' } },
      { path: '/public/demo/test/rate-limit-status', method: 'GET' },
      { path: '/public/demo/test/database-stats', method: 'GET' },
    ];

    const results = await Promise.all(
      endpoints.map(({ path, method, body }) => testEndpoint(path, method, body))
    );
    
    setEndpointHealth(results);
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(), 
      fetchUsers(), 
      fetchRecentActivity(), 
      fetchRequestOrigins(), 
      testAllEndpoints()
    ]);
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    refreshData();
    
    if (autoRefresh) {
      const interval = setInterval(refreshData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusColor = (status: EndpointHealth['status']) => {
    switch (status) {
      case 'healthy': return 'text-green-500';
      case 'slow': return 'text-yellow-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: EndpointHealth['status']) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'slow': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  // Robust date parsing for values coming from D1 (e.g., "YYYY-MM-DD HH:MM:SS")
  const parseSessionDate = (raw: string): Date | null => {
    if (!raw) return null;
    const value = String(raw).trim();
    // If already ISO or has timezone info, parse directly
    if (value.endsWith('Z') || value.includes('+')) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    // Try treating as UTC by converting space to 'T' and appending 'Z'
    const isoUtc = value.replace(' ', 'T') + 'Z';
    const dUtc = new Date(isoUtc);
    if (!isNaN(dUtc.getTime())) return dUtc;
    // Fallback: try local ISO (without Z)
    const isoLocal = value.replace(' ', 'T');
    const dLocal = new Date(isoLocal);
    if (!isNaN(dLocal.getTime())) return dLocal;
    // Final fallback
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = parseSessionDate(dateString) || new Date();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getUserAgentInfo = (userAgent: string) => {
    if (!userAgent) return { browser: 'Unknown', isBot: false };
    
    const isBot = /bot|crawler|spider|scraper|curl|postman|insomnia/i.test(userAgent);
    
    if (userAgent.includes('curl')) return { browser: 'cURL', isBot: true };
    if (userAgent.includes('Postman')) return { browser: 'Postman', isBot: true };
    if (userAgent.includes('Insomnia')) return { browser: 'Insomnia', isBot: true };
    if (userAgent.includes('Chrome')) return { browser: 'Chrome', isBot: false };
    if (userAgent.includes('Firefox')) return { browser: 'Firefox', isBot: false };
    if (userAgent.includes('Safari')) return { browser: 'Safari', isBot: false };
    if (userAgent.includes('Edge')) return { browser: 'Edge', isBot: false };
    
    return { browser: 'Other', isBot };
  };

  // Chart datasets
  const vpnPieData = stats
    ? [
        { name: 'VPN Users', value: stats.usage_tracking.vpn_users },
        { name: 'Direct Users', value: Math.max(0, stats.usage_tracking.total_users - stats.usage_tracking.vpn_users) }
      ]
    : [];

  const COLORS = ['#ef4444', '#10b981', '#3b82f6', '#a855f7', '#f59e0b'];

  const countryBarData = (stats?.top_countries || []).slice(0, 7).map((c) => ({
    country: c.country_code,
    sessions: c.session_count,
    minutes: c.minute_count
  }));

  // Build activity per hour for last 24 hours (based on recentActivity payload we already fetch)
  const activityLineData = (() => {
    const now = new Date();
    const buckets: Record<string, number> = {};
    for (let i = 23; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setHours(now.getHours() - i, 0, 0, 0);
      const key = d.toISOString().slice(11, 13); // HH
      buckets[key] = 0;
    }
    recentActivity.forEach((a) => {
      const d = new Date(a.created_at);
      const key = d.toISOString().slice(11, 13);
      if (key in buckets) buckets[key] += 1;
    });
    return Object.entries(buckets).map(([hour, count]) => ({ hour, sessions: count }));
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Top Navigation Bar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm sticky top-0 z-50">
        <div className="max-w-[1920px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left Section - Logo and Title */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Platform Monitor</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Real-time Analytics Dashboard</p>
                </div>
              </div>
              
              {/* Navigation Tabs */}
              <div className="hidden lg:flex items-center gap-1 ml-8">
                {[
                  { id: 'overview', label: 'Overview', icon: Activity },
                  { id: 'users', label: 'Users', icon: Users },
                  { id: 'activity', label: 'Live Activity', icon: Clock },
                  { id: 'origins', label: 'Request Origins', icon: Globe }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    type="button"
                    key={id}
                    onClick={() => setActiveTab(id as typeof activeTab)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      activeTab === id
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Section - Controls */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-3 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-600 dark:text-gray-400">Auto-refresh</span>
                </label>
                
                <div className="text-gray-400">|</div>
                
                <span className="text-gray-500 dark:text-gray-400">
                  Updated: <span className="font-medium text-gray-700 dark:text-gray-300">{lastUpdated.toLocaleTimeString()}</span>
                </span>
              </div>
              
              <button
                type="button"
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              <button
                type="button"
                onClick={refreshData}
                disabled={loading}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 transition-all duration-200 shadow-md"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="font-medium">Refresh</span>
              </button>
            </div>
          </div>
          
          {/* Mobile Navigation */}
          <div className="lg:hidden flex items-center gap-2 pb-3 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'activity', label: 'Activity', icon: Clock },
              { id: 'origins', label: 'Origins', icon: Globe }
            ].map(({ id, label, icon: Icon }) => (
              <button
                type="button"
                key={id}
                onClick={() => setActiveTab(id as typeof activeTab)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-[1920px] mx-auto px-4 lg:px-8 py-6">

        {loading && !stats ? (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-600 dark:text-blue-400" />
            <p className="text-gray-600 dark:text-gray-400">Loading monitoring data...</p>
          </div>
        ) : (
          <div className="space-y-8">
            
            {/* Tab Content */}
            {activeTab === 'overview' && (
              <>
                {/* Endpoint Health Status */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-50 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                        <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      Endpoint Health
                    </h2>
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {endpointHealth.filter(e => e.status === 'healthy').length}/{endpointHealth.length} Healthy
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {endpointHealth.map((endpoint, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className={`flex items-center gap-2 ${getStatusColor(endpoint.status)}`}>
                            {getStatusIcon(endpoint.status)}
                            <span className="font-medium text-sm capitalize">{endpoint.status}</span>
                          </div>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 px-2 py-1 rounded">{endpoint.responseTime}ms</span>
                        </div>
                        <p className="text-xs font-mono text-gray-600 dark:text-gray-400 break-all">{endpoint.endpoint}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                          {new Date(endpoint.lastChecked).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Usage Statistics */}
                {stats && (
                  <>
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-lg dark:hover:shadow-gray-900/50 transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {stats.usage_tracking.total_users.toLocaleString()}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Users</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Unique IPs tracked</p>
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">Active this week</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-lg dark:hover:shadow-gray-900/50 transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-green-50 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                            <Clock className="w-6 h-6 text-green-600 dark:text-green-400" />
                          </div>
                          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {stats.usage_tracking.total_minutes_used.toLocaleString()}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Minutes</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Video chat time</p>
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">{stats.usage_tracking.avg_minutes_per_user.toFixed(1)} avg/user</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-lg dark:hover:shadow-gray-900/50 transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                            <Activity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {stats.sessions.total_sessions.toLocaleString()}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Sessions</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Demo sessions created</p>
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">{stats.sessions.active_sessions} active now</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-lg dark:hover:shadow-gray-900/50 transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-red-50 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                            <Shield className="w-6 h-6 text-red-600 dark:text-red-400" />
                          </div>
                          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {stats.usage_tracking.vpn_users.toLocaleString()}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">VPN Users</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Detected & limited</p>
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">{stats.usage_tracking.users_hit_limits} hit limits</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      
                      {/* Usage Breakdown */}
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          Usage Analytics
                        </h3>
                    <div className="grid grid-cols-1 gap-6">
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={vpnPieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                              {vpnPieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 text-center">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Avg Minutes/User</div>
                          <div className="text-lg font-semibold dark:text-gray-100">{stats.usage_tracking.avg_minutes_per_user.toFixed(1)}</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 text-center">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Avg Sessions/User</div>
                          <div className="text-lg font-semibold dark:text-gray-100">{stats.usage_tracking.avg_sessions_per_user.toFixed(1)}</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 text-center">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Users Hit Limits</div>
                          <div className="text-lg font-semibold text-red-600 dark:text-red-400">{stats.usage_tracking.users_hit_limits}</div>
                        </div>
                      </div>
                    </div>
                      </div>

                      {/* Geographic Distribution */}
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                            <Globe className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          Geographic Distribution
                        </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={countryBarData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="country" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="sessions" fill="#3b82f6" name="Sessions" />
                          <Bar dataKey="minutes" fill="#a855f7" name="Minutes" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                      </div>
                    </div>

                {/* Activity Over Time */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-50 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    Session Activity
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-1 rounded-full ml-auto">Last 24h</span>
                  </h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={activityLineData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="sessions" stroke="#10b981" strokeWidth={2} dot={false} name="Sessions" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                  </>
                )}
              </>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    User Analytics
                  </h2>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 px-3 py-1.5 rounded-full">
                    {users.length} Total Users
                  </span>
                </div>
                
                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">IP Hash</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">Country</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">Sessions</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">Minutes</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">VPN</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">First Seen</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-sm">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr key={index} className="border-b border-gray-50 dark:border-gray-700 hover:bg-blue-50/30 dark:hover:bg-blue-900/20 transition-colors">
                          <td className="py-3 px-4">
                            <div className="font-mono text-xs text-gray-700 dark:text-gray-300 font-medium">
                              {user.ip_hash.substring(0, 12)}...
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {user.fingerprint_hash?.substring(0, 8)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700">
                              {user.country_code || 'Unknown'}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-semibold text-gray-900 dark:text-gray-100 text-sm">{user.session_count}</td>
                          <td className="py-3 px-4 font-semibold text-gray-900 dark:text-gray-100 text-sm">{user.total_minutes}</td>
                          <td className="py-3 px-4">
                            {user.vpn_detected ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-700">
                                <Shield className="w-3 h-3 mr-1" />
                                VPN
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700">
                                Direct
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {user.limit_reached ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-orange-50 text-orange-700">
                                Limited
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400">
                            {formatTimeAgo(user.first_session)}
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400">
                            {formatTimeAgo(user.last_session)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {users.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No users found. Start by creating demo sessions.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Live Activity Tab */}
            {activeTab === 'activity' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    Recent Activity
                  </h2>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 px-3 py-1.5 rounded-full">
                    {recentActivity.length} Sessions
                  </span>
                </div>
                
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                              {activity.host_name} & {activity.guest_name}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                              {activity.country_code}
                            </span>
                            {activity.vpn_detected && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-700">
                                <Shield className="w-3 h-3 mr-1" />
                                VPN
                              </span>
                            )}
                          </div>
                          
                          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            <div>Session ID: <span className="font-mono">{activity.session_id}</span></div>
                            <div>Room ID: <span className="font-mono">{activity.room_id}</span></div>
                            <div>IP: <span className="font-mono">{activity.ip_hash.substring(0, 12)}...</span></div>
                            {activity.user_agent && (
                              <div>Browser: <span className="font-mono">{getUserAgentInfo(activity.user_agent).browser}</span></div>
                            )}
                            <div>Source: <span className="font-mono text-xs">Direct Traffic</span></div>
                          </div>
                        </div>
                        
                        <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                          {formatTimeAgo(activity.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {recentActivity.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No recent activity. Sessions will appear here as they're created.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Request Origins Tab */}
            {activeTab === 'origins' && requestOrigins && (
              <div className="space-y-4">
                {/* Referrers */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-50 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    Request Referrers
                  </h3>
                  <div className="space-y-3">
                    {requestOrigins.referrers.map((ref, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">
                            {ref.referrer || 'Direct Traffic'}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-semibold dark:text-gray-200">{ref.count} requests</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{ref.percentage.toFixed(1)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Agents */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                      <Zap className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    User Agents & Tools
                  </h3>
                  <div className="space-y-3">
                    {requestOrigins.user_agents.map((ua, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              {getUserAgentInfo(ua.user_agent).browser}
                            </span>
                            {ua.is_bot && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                                Bot/Tool
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                            {ua.user_agent}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-right dark:text-gray-200">
                          {ua.count} requests
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Automation Tools */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    Detected Automation Tools
                  </h3>
                  <div className="space-y-3">
                    {requestOrigins.automation_tools.map((tool, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 dark:text-gray-100">{tool.tool_name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Last seen: {formatTimeAgo(tool.last_seen)}</div>
                        </div>
                        <div className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                          {tool.count} requests
                        </div>
                      </div>
                    ))}
                    
                    {requestOrigins.automation_tools.length === 0 && (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                        No automation tools detected yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Integration Examples */}
            {activeTab === 'overview' && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center">
                    <Zap className="w-4 h-4 text-yellow-600" />
                  </div>
                  Integration Examples
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* cURL Example */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-800 mb-3 text-sm">cURL / API Testing</h4>
                    <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono overflow-x-auto">
                      <div className="mb-2"># Create demo session</div>
                      <div>curl -X POST \</div>
                      <div className="ml-2">https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/create-session \</div>
                      <div className="ml-2">-H "Content-Type: application/json" \</div>
                      <div className="ml-2">-d '&#123;"hostName":"John","guestName":"Jane"&#125;'</div>
                    </div>
                  </div>

                  {/* Zapier/n8n Example */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-800 mb-3 text-sm">Zapier / n8n Webhook</h4>
                    <div className="bg-blue-50 p-3 rounded-lg text-sm">
                      <div className="mb-2"><strong>Webhook URL:</strong></div>
                      <div className="font-mono text-xs break-all bg-white p-2 rounded-lg border border-blue-100">
                        https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/create-session
                      </div>
                      <div className="mt-2"><strong>Method:</strong> POST</div>
                      <div><strong>Content-Type:</strong> application/json</div>
                    </div>
                  </div>
                </div>

                {/* API Endpoints Reference */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="font-medium text-gray-800 mb-4 text-sm">Available Endpoints</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div className="font-mono bg-gray-50 p-2.5 rounded-lg">
                      <span className="text-green-600 font-semibold">POST</span> /public/demo/create-session
                    </div>
                    <div className="font-mono bg-gray-50 p-2.5 rounded-lg">
                      <span className="text-blue-600 font-semibold">GET</span> /public/demo/status/check
                    </div>
                    <div className="font-mono bg-gray-50 p-2.5 rounded-lg">
                      <span className="text-purple-600 font-semibold">POST</span> /public/demo/end-session/&#123;id&#125;
                    </div>
                    <div className="font-mono bg-gray-50 p-2.5 rounded-lg">
                      <span className="text-blue-600 font-semibold">GET</span> /public/demo/test/database-stats
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pb-6 text-center">
          <div className="inline-flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              Real-time monitoring
            </span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>Updates every 30 seconds</span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <a 
              href="https://github.com/your-repo" 
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 inline-flex items-center gap-1 font-medium"
            >
              API Documentation <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDemoMonitor;
